import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Form, Modal, Button, Collapse, Spin, Input } from 'antd';

import { PdfViewerComponent } from '../../../shared/components/pdf-viewer';
import FieldItem from './field-item';
import MappingTree from '../mapping-tree';
import * as mappingConstants from '../store/constants/mapping-constants';

import { isEmpty, isNumber } from 'lodash';

const { Panel } = Collapse;

const mappingModal = ({
  rootContainer,
  userMappingObjects,
  setNewUserMappings,
  loading,
  isPreview,
  fileBytes,
  registerFormSaveCallback,
  onPdfLoadedEvent,
  fileEditorLoading,
  setIsDocumentChanged,
}) => {
  const [mappingNameForm] = Form.useForm();

  const inputRef = useRef(null);

  const [showMappingModal, setShowMappingModal] = useState(false);
  const [cleanModalData, setCleanModalData] = useState(false);
  const [targetName, setTargetName] = useState();
  const [currentMappedField, setCurrentMappedField] = useState({});
  const [targetFocus, setTargetFocus] = useState();
  const [userObjectType, setUserObjectType] = useState();
  const [defaultValue, setDefaultValue] = useState();
  const [insuredMappingExists, setInsuredMappingExists] = useState(false);
  const [prospectMappingExists, setProspectMappingExists] = useState(false);
  const [policyMappingExists, setPolicyMappingExists] = useState(false);
  const [quoteMappingExists, setQuoteMappingExists] = useState(false);

  const mappingObjectNames = useSelector((state) => state.reverseMapping.mappingObjectNames);
  const mappingObjectNamesLoading = useSelector((state) => state.reverseMapping.mappingObjectNamesLoading);

  useEffect(() => {
    showMappingModal ? (document.body.style.overflow = 'hidden') : (document.body.style.overflow = 'unset');
  }, [showMappingModal]);

  const onFormEditorClick = (event) => {
    const tagName = event?.target?.type;

    if (
      !tagName ||
      (tagName !== 'text' &&
        tagName !== 'checkbox' &&
        tagName !== 'textarea' &&
        tagName !== 'radio' &&
        tagName !== 'select-multiple' &&
        tagName !== 'select-one')
    ) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    inputRef.current = event.target;

    if (tagName === 'text' || tagName === 'textarea') {
      setDefaultValue(event.target.value);
    }

    setTargetName(event.target.name);

    const insuredIndex = userMappingObjects.findIndex((element) => element.objectKey === 'Insured');
    setInsuredMappingExists(insuredIndex !== -1);

    const prospectIndex = userMappingObjects.findIndex((element) => element.objectKey === 'Prospect');
    setProspectMappingExists(prospectIndex !== -1);

    const policyIndex = userMappingObjects.findIndex((element) => element.objectKey === 'Policy');
    setPolicyMappingExists(policyIndex !== -1);

    const quoteIndex = userMappingObjects.findIndex((element) => element.objectKey === 'Quote');
    setQuoteMappingExists(quoteIndex !== -1);

    const obj = userMappingObjects.find((element) => element.formFieldName === event.target.name);

    setCurrentMappedField(obj);

    setCleanModalData(false);
    setShowMappingModal(true);
  };

  const onCancel = () => {
    setShowMappingModal(false);
  };

  const onOk = () => {
    mappingNameForm.validateFields().then((result) => {
      updateUserMapping(result);
      setShowMappingModal(false);
    });
  };

  const updateUserMapping = (formsFields) => {
    let newMappings = userMappingObjects.slice();
    let level = 0;

    if (formsFields) {
      let mappedObj = getUserMappingObjectFromFormFields(formsFields, level);

      if (mappedObj) {
        mappedObj.formFieldName = targetName;
        mappedObj.type = userObjectType;

        const objIndex = newMappings.findIndex((element) => element.formFieldName == mappedObj.formFieldName || isEqualObjects(element, mappedObj));

        if (newMappings.length > 0 && objIndex !== -1) {
          const element = document.getElementsByName(newMappings[objIndex].formFieldName)[0];
          if (element) {
            element.style.backgroundColor = 'transparent';
          }

          newMappings[objIndex] = mappedObj;
        } else {
          newMappings.push(mappedObj);
        }
      } else {
        const objIndex = newMappings.findIndex((element) => element.formFieldName == targetName);

        if (objIndex !== -1) {
          const element = document.getElementsByName(newMappings[objIndex].formFieldName)[0];
          if (element) {
            element.style.backgroundColor = 'transparent';
          }

          newMappings = newMappings.filter((value) => {
            return value.formFieldName != targetName;
          });
        }
      }

      setNewUserMappings(newMappings);
    }
  };

  const getUserMappingObjectFromFormFields = (formsFields, level) => {
    let childMapping;

    if (!isEmpty(formsFields[mappingConstants.OBJECT_FIELD_NAME + level])) {
      childMapping = {};
      childMapping.objectKey = formsFields[mappingConstants.OBJECT_FIELD_NAME + level];
      childMapping.objectPosition = formsFields[mappingConstants.POSITION_FIELD_NAME + level];
      childMapping.childMapping = getUserMappingObjectFromFormFields(formsFields, level + 1);
    }

    return childMapping;
  };

  const isEqualObjects = (element, newElement) => {
    return (
      element.objectKey == newElement.objectKey &&
      element.objectPosition == newElement.objectPosition &&
      (!(element.childMapping && newElement.childMapping) || isEqualObjects(element.childMapping, newElement.childMapping))
    );
  };

  const afterCloseModal = () => {
    mappingNameForm.resetFields();
    setTargetName();
    setCurrentMappedField();
    setUserObjectType();
    setCleanModalData(true);

    if (inputRef.current) {
      inputRef.current.blur();
      inputRef.current = null;
    }

    if (targetFocus) {
      targetFocus.focus();

      targetFocus.classList.add('blink-background');

      const timer = setInterval(() => {
        clearInterval(timer);
        targetFocus.classList.remove('blink-background');
      }, 1000);

      setTargetFocus();
    }
  };

  const getStringMappingObjectFromFormFields = (formsFields, level) => {
    let addStringMap;

    if (!isEmpty(formsFields[mappingConstants.OBJECT_FIELD_NAME + level])) {
      addStringMap = formsFields[mappingConstants.OBJECT_FIELD_NAME + level];

      if (isNumber(formsFields[mappingConstants.POSITION_FIELD_NAME + level])) {
        addStringMap += '[' + formsFields[mappingConstants.POSITION_FIELD_NAME + level] + ']';
      }

      let childAddStringMap = getStringMappingObjectFromFormFields(formsFields, level + 1);

      if (!isEmpty(childAddStringMap)) {
        addStringMap += '.' + childAddStringMap;
      }
    }

    return addStringMap;
  };

  const onDefaultValueChange = (e) => {
    if (inputRef.current) {
      setDefaultValue(e.target.value);
      inputRef.current.value = e.target.value;
      inputRef.current.dispatchEvent(new Event('input', { bubbles: true }));

      if (typeof setIsDocumentChanged == 'function') {
        setIsDocumentChanged(true);
      }
    }
  };

  return (
    <>
      <PdfViewerComponent
        rootContainer={rootContainer}
        fileBytes={fileBytes}
        onFormEditorClick={onFormEditorClick}
        registerFormSaveCallback={registerFormSaveCallback}
        onPdfLoadedEvent={onPdfLoadedEvent}
        loading={fileEditorLoading}
        disableDeletePages={isPreview}
      />

      {cleanModalData ? undefined : (
        <Modal
          className="mapping-modal"
          title="Map Field To Object"
          open={showMappingModal}
          getContainer={rootContainer}
          centered
          width={800}
          onCancel={onCancel}
          onOk={isPreview ? onOk : undefined}
          okText="Save"
          okButtonProps={{ loading }}
          afterClose={afterCloseModal}
          footer={[
            <Button key="back" onClick={onCancel}>
              Cancel
            </Button>,
            <Button key="submit" type="primary" loading={loading} onClick={onOk} disabled={isPreview}>
              Ok
            </Button>,
          ]}
        >
          {!showMappingModal || mappingObjectNamesLoading ? (
            <Spin spinning />
          ) : (
            <>
              <Form name="mapping_name_form" form={mappingNameForm} size="small">
                <Collapse size="small" defaultActiveKey={1}>
                  <Panel header="Map the selected field to the following object property" key="1" size="small">
                    <FieldItem
                      originalMappingObjects={mappingObjectNames}
                      rootContainer={rootContainer}
                      level={0}
                      mappingNameForm={mappingNameForm}
                      skipRequired={true}
                      setUserObjectType={setUserObjectType}
                      currentMappedField={currentMappedField}
                      setCurrentMappedField={setCurrentMappedField}
                      isPreview={isPreview}
                      insuredMappingExists={insuredMappingExists}
                      prospectMappingExists={prospectMappingExists}
                      policyMappingExists={policyMappingExists}
                      quoteMappingExists={quoteMappingExists}
                    />

                    {inputRef.current && (inputRef.current.type === 'text' || inputRef.current.type === 'textarea') && !isPreview ? (
                      <Form.Item className="mapping-item" label="Default value">
                        <Input placeholder="Default value" allowClear value={defaultValue} onChange={onDefaultValueChange} />
                      </Form.Item>
                    ) : null}
                  </Panel>
                </Collapse>
              </Form>

              <Collapse size="small">
                <Panel header="Mapped Objects" key="1" size="small">
                  <MappingTree
                    originalMappingObjects={mappingObjectNames}
                    userMappingObjects={userMappingObjects}
                    setTargetFocus={setTargetFocus}
                    setShowMappingModal={setShowMappingModal}
                  />
                </Panel>
              </Collapse>
            </>
          )}
        </Modal>
      )}
    </>
  );
};

export default mappingModal;
